import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getRankBadgeStyle } from '../../styling/stylingHelper';
import { NoDataInPeriod } from '../ui/NoDataInPeriod';

export interface RankingItem {
  name: string;
  value: string;
}

interface RankingCardProps {
  icon: React.ReactNode;
  title: string;
  items: RankingItem[];
  onViewFull: () => void;
  /** Independent loading state — renders skeleton rows without blocking the page. */
  loading?: boolean;
  /** Shown when there are no items and not loading. */
  emptyText?: string;
}

const SKELETON_ROWS = 5;

export const RankingCard: React.FC<RankingCardProps> = ({
  icon,
  title,
  items,
  onViewFull,
  loading = false,
  emptyText,
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white dark:bg-card-dark rounded-xl p-4 flex flex-col shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          {icon}
        </span>
        <span className="text-[18px] leading-6 font-semibold text-textPrimary dark:text-textPrimary-dark truncate">
          {title}
        </span>
      </div>

      {/* Items list */}
      <div className="flex flex-col gap-2 mb-2 flex-1">
        {loading ? (
          Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 border-b border-border dark:border-border-dark p-2">
              <span className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0" />
              <span className="flex-1 h-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
              <span className="w-12 h-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
            </div>
          ))
        ) : items.length === 0 ? (
          emptyText ? (
            <div className="flex items-center justify-center flex-1 py-6 text-sm text-muted dark:text-muted-dark">
              {emptyText}
            </div>
          ) : (
            <NoDataInPeriod className="flex-1" />
          )
        ) : (
          items.map((item, i) => {
            const rank = i + 1;
            return (
              <div key={i} className="flex items-center gap-3 border-b border-border dark:border-border-dark p-2">
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[12px] tracking-[0.06px] font-bold flex-shrink-0"
                  style={getRankBadgeStyle(rank)}
                >
                  {rank}
                </span>
                <span className="flex-1 text-[14px] leading-5 font-semibold text-textPrimary dark:text-textPrimary-dark truncate">
                  {item.name}
                </span>
                <span className="text-[14px] leading-5 font-semibold text-textSecondary dark:text-textSecondary-dark flex-shrink-0">
                  {item.value}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Footer link */}
      <button
        onClick={onViewFull}
        className="w-full flex items-center justify-between p-2 gap-1.5 text-[#079455] dark:text-textGreen-dark text-[14px] leading-5 font-semibold hover:opacity-80 transition mt-auto"
      >
        {t('dashboard.viewFullRanking')}
        <ArrowRight className="w-4 h-4 flex-shrink-0" />
      </button>
    </div>
  );
};

export default RankingCard;
